<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import type { GetEditorialSearchResultsProjectResponse } from '@/api';
import { getBathroomsRange, getBedroomsRange, getImageByPriority, getMinPrice } from '@/views/search/index.ts';
import { GoogleMapsMapId, loadGoogleMaps } from '@/utilities/google-maps.ts';
import { useTracking } from '@/utilities/tracking.ts';
import { Constants } from '@/utilities/constants.ts';
import { getCustomIcon } from '@/common/custom-icons.ts';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faBath, faBed } from '@fortawesome/pro-solid-svg-icons';

const props = defineProps<{
  projects: GetEditorialSearchResultsProjectResponse[];
  priceTypeId?: number | null;
}>();

const emit = defineEmits<{
  select: [projectId: GetEditorialSearchResultsProjectResponse['id']];
}>();

const { locale, t } = useI18n();
const { trackAction } = useTracking();

const mapElement = ref<HTMLElement | null>(null);
const infoWindowContent = ref<HTMLElement | null>(null);
const mapError = ref('');
const selectedProject = ref<GetEditorialSearchResultsProjectResponse | null>(null);

const map = shallowRef<google.maps.Map | null>(null);
const infoWindow = shallowRef<google.maps.InfoWindow | null>(null);
const markers = shallowRef<google.maps.marker.AdvancedMarkerElement[]>([]);
const listeners: google.maps.MapsEventListener[] = [];
const markerListeners: google.maps.MapsEventListener[] = [];
let resizeObserver: ResizeObserver | null = null;

// Un projet sans adresse civique garde des coordonnées approximatives : on ne l'épingle pas, on le liste sous la carte
const hasPreciseAddress = (project: GetEditorialSearchResultsProjectResponse) => project.address.addressLine1.trim().length > 0;

const mappableProjects = computed(() => props.projects.filter(hasPreciseAddress));
const pendingAddressProjects = computed(() => props.projects.filter((project) => !hasPreciseAddress(project)));

const selectedProjectImage = computed(() => (selectedProject.value ? getImageByPriority(selectedProject.value) : null));
const selectedBedroomsRange = computed(() => (selectedProject.value ? getBedroomsRange(selectedProject.value) : undefined));
const selectedBathroomsRange = computed(() => (selectedProject.value ? getBathroomsRange(selectedProject.value) : undefined));
const selectedMinPrice = computed(() => (selectedProject.value ? getMinPrice(selectedProject.value, props.priceTypeId) : undefined));
const selectedTags = computed(() => selectedProject.value?.tags.slice(0, 5) ?? []);
const selectedHasCharacteristics = computed(() => Boolean(selectedBedroomsRange.value || selectedBathroomsRange.value || selectedTags.value.length > 0));

const getProjectRoute = (project: GetEditorialSearchResultsProjectResponse) => ({
  name: 'project',
  params: { slug: project.name.toSlug(), projectId: project.id },
});

const clearMarkers = () => {
  // Seuls les écouteurs des marqueurs sont retirés : ceux de la carte et de l'infobulle doivent survivre
  for (const listener of markerListeners.splice(0)) {
    listener.remove();
  }

  for (const marker of markers.value) {
    marker.map = null;
  }

  markers.value = [];
};

const openInfoWindow = (project: GetEditorialSearchResultsProjectResponse, marker: google.maps.marker.AdvancedMarkerElement) => {
  selectedProject.value = project;

  if (map.value && infoWindow.value && infoWindowContent.value) {
    infoWindow.value.setContent(infoWindowContent.value);
    infoWindow.value.open({ map: map.value, anchor: marker });
  }

  emit('select', project.id);

  void trackAction('project', project.id, 'mapClick');
};

// Le cadrage est rejoué quand le conteneur change de taille : la colonne en flexbox
// n'a pas toujours sa hauteur finale au moment où la carte est créée, et les pins
// se retrouveraient alors hors champ
const applyFraming = shallowRef<(() => void) | null>(null);

const renderMarkers = (google: typeof globalThis.google) => {
  clearMarkers();

  if (!map.value) {
    return;
  }

  const bounds = new google.maps.LatLngBounds();
  const created: google.maps.marker.AdvancedMarkerElement[] = [];

  for (const project of mappableProjects.value) {
    const position = { lat: project.coordinates.latitude, lng: project.coordinates.longitude };
    const pin = new google.maps.marker.PinElement({
      background: '#16a34a',
      borderColor: '#15803d',
      glyphColor: '#ffffff',
      scale: 1,
    });

    const marker = new google.maps.marker.AdvancedMarkerElement({
      map: map.value,
      position,
      title: project.name,
      // pin.element plutôt que pin : passer le PinElement directement n'affiche aucun marqueur sur cette version de l'API
      content: pin.element,
    });

    markerListeners.push(marker.addListener('click', () => openInfoWindow(project, marker)));
    created.push(marker);
    bounds.extend(position);
  }

  markers.value = created;

  const only = created.length === 1 ? mappableProjects.value[0] : null;

  applyFraming.value = () => {
    if (!map.value) {
      return;
    }

    if (only) {
      map.value.setCenter({ lat: only.coordinates.latitude, lng: only.coordinates.longitude });
      map.value.setZoom(only.coordinates.zoom);
      return;
    }

    if (created.length > 1) {
      map.value.fitBounds(bounds, 48);
    }
  };

  applyFraming.value();
};

onMounted(async () => {
  if (!mapElement.value) {
    return;
  }

  try {
    const google = await loadGoogleMaps(locale.value);

    map.value = new google.maps.Map(mapElement.value, {
      center: { lat: Constants.DefaultMapCenter.Latitude, lng: Constants.DefaultMapCenter.Longitude },
      zoom: Constants.DefaultMapCenter.Zoom,
      mapId: GoogleMapsMapId,
      cameraControl: false,
      fullscreenControl: false,
      mapTypeControl: false,
      streetViewControl: false,
      zoomControl: true,
    });

    infoWindow.value = new google.maps.InfoWindow({ maxWidth: 260 });
    listeners.push(infoWindow.value.addListener('closeclick', () => (selectedProject.value = null)));

    // Le cadrage doit être rejoué une fois la carte stabilisée : appliqué trop tôt, il part
    // d'un conteneur qui n'a pas sa taille finale et laisse tous les pins hors champ
    listeners.push(
      google.maps.event.addListenerOnce(map.value, 'idle', () => {
        applyFraming.value?.();
      })
    );

    renderMarkers(google);

    resizeObserver = new ResizeObserver(() => applyFraming.value?.());
    resizeObserver.observe(mapElement.value);
  } catch (error) {
    console.error('Error loading the projects map:', error);
    mapError.value = t('views.editorial.map.error');
  }
});

watch(
  () => props.projects,
  () => {
    if (window.google?.maps && map.value) {
      infoWindow.value?.close();
      selectedProject.value = null;
      renderMarkers(window.google);
    }
  }
);

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  resizeObserver = null;
  infoWindow.value?.close();
  clearMarkers();

  for (const listener of listeners.splice(0)) {
    listener.remove();
  }

  applyFraming.value = null;
  map.value = null;
});
</script>

<template>
  <!-- Colonne bornée à la hauteur de l'écran : la carte s'étire, la liste sous la carte reste toujours atteignable -->
  <div class="flex flex-col gap-4 lg:h-[calc(100vh-6rem)]">
    <div class="relative overflow-hidden rounded-lg border-2 border-gray-200 bg-gray-50 lg:min-h-0 lg:flex-1">
      <div ref="mapElement" class="h-[65vh] w-full lg:h-full" />
      <div v-if="mapError" class="absolute inset-0 grid place-items-center bg-white/90 px-6 text-center text-sm font-medium text-red-700">
        {{ mapError }}
      </div>
    </div>

    <section v-if="pendingAddressProjects.length > 0" class="shrink-0 rounded-lg border border-gray-200 p-4 lg:max-h-44 lg:overflow-y-auto">
      <h3 class="mb-1 text-sm font-semibold text-gray-900">{{ t('views.editorial.map.pending-address') }}</h3>
      <p class="mb-3 text-xs text-gray-500">{{ t('views.editorial.map.pending-address-description') }}</p>
      <ul class="space-y-1" role="list">
        <li v-for="project in pendingAddressProjects" :key="project.id">
          <RouterLink :to="getProjectRoute(project)" class="hover:text-ph-green-700 text-sm text-gray-700 underline underline-offset-2">
            {{ project.name }}
          </RouterLink>
          <span class="text-xs text-gray-500"> &ndash; {{ project.address.city.name }}, {{ project.address.province }}</span>
        </li>
      </ul>
    </section>

    <!-- Contenu de l'infobulle : Google déplace le noeud interne dans la carte, le parent masqué ne le suit pas -->
    <div class="hidden">
      <div ref="infoWindowContent">
        <div v-if="selectedProject" class="w-64">
          <img v-if="selectedProjectImage" :alt="selectedProjectImage.title" :src="selectedProjectImage.src.resizedToSmall()" class="mb-2 aspect-3/2 w-full rounded-md object-cover" />
          <p class="text-sm font-semibold text-gray-900">{{ selectedProject.name }}</p>
          <p class="text-xs text-gray-500">{{ selectedProject.address.city.name }}, {{ selectedProject.address.province }}</p>

          <ul v-if="selectedHasCharacteristics" class="mt-2 flex flex-wrap gap-1.5 text-xs" role="list">
            <li v-if="selectedBedroomsRange" class="flex h-7 items-center rounded-md bg-gray-200 px-2">
              <FontAwesomeIcon :icon="faBed" class="mr-1 h-3.5 text-gray-500" />
              <span>
                <template v-if="selectedBedroomsRange.hasStudio">
                  {{ t('views.search.results.studio') }}<template v-if="selectedBedroomsRange.max"> &ndash; {{ selectedBedroomsRange.max }} </template>
                </template>
                <template v-else>
                  {{ selectedBedroomsRange.min }}<template v-if="selectedBedroomsRange.max !== selectedBedroomsRange.min"> &ndash; {{ selectedBedroomsRange.max }} </template>
                </template>
              </span>
            </li>
            <li v-if="selectedBathroomsRange" class="flex h-7 items-center rounded-md bg-gray-200 px-2">
              <FontAwesomeIcon :icon="faBath" class="mr-1 h-3.5 text-gray-500" />
              <span>
                {{ selectedBathroomsRange.min }}<template v-if="selectedBathroomsRange.max !== selectedBathroomsRange.min"> &ndash; {{ selectedBathroomsRange.max }} </template>
              </span>
            </li>
            <li v-for="item in selectedTags" :key="item.id" class="flex h-7 items-center rounded-md bg-gray-200 px-2">
              <FontAwesomeIcon :icon="getCustomIcon(item.customIcon)" class="h-3.5 text-gray-500" />
            </li>
          </ul>

          <p class="mt-2 text-sm text-gray-800">
            <template v-if="selectedProject.isPaid && selectedMinPrice">
              <span v-if="selectedMinPrice.isStarting" class="pr-1 text-xs text-gray-500">
                {{ t('views.search.results.starting-from') }}
              </span>
              <span class="font-semibold">
                {{ selectedMinPrice.amount.formatCurrency(locale) }}
              </span>
              <span v-if="selectedMinPrice.type.id == Constants.PriceTypeId.ForRent" class="pl-1 text-xs">
                {{ t('views.search.results.per-month') }}
              </span>
            </template>
            <template v-else>
              <span class="text-xs text-gray-500">
                {{ t('views.search.results.upon-request') }}
              </span>
            </template>
          </p>

          <RouterLink :to="getProjectRoute(selectedProject)" class="text-ph-green-700 hover:text-ph-green-800 mt-2 inline-block text-sm font-medium underline underline-offset-2">
            {{ t('views.editorial.map.view-project') }}
          </RouterLink>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="css" scoped></style>
